import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Card, CardContent,
  ToggleButton, ToggleButtonGroup, Tabs, Tab, TablePagination, Alert, Button, CircularProgress, Chip,
} from '@mui/material';
import {
  SmartToy as AiIcon,
  Business as TcIcon,
  Forum as SessionsIcon,
  Chat as ChatIcon,
  Token as TokenIcon,
  AttachMoney as CostIcon,
  ErrorOutline as ErrorIcon,
  Speed as SpeedIcon,
  BuildCircle as ToolIcon,
} from '@mui/icons-material';
import aiAssistantService from '../../services/ai/aiAssistant.service';
import { getBrandColor, getBrandedTableHeadSx } from '../../utils/colorUtils';

const PERIODS = [7, 30, 90];
const PAGE_SIZE = 20;

const formatTokens = (n) => (n >= 1000000 ? `${(n / 1000000).toFixed(1)}M` : n >= 1000 ? `${(n / 1000).toFixed(1)}K` : `${n}`);
const formatCost = (amount) => `$${Number(amount || 0).toFixed(2)}`;
const formatRate = (rate) => `${(Number(rate || 0) * 100).toFixed(1)}%`;
const formatMs = (ms) => (ms == null ? '—' : ms >= 1000 ? `${(ms / 1000).toFixed(1)} s` : `${ms} ms`);
const errorText = (err) => err?.response?.data?.error || err?.response?.data?.message || 'Could not load platform AI usage';
const cellSx = { fontSize: '0.8rem' };
const headCellSx = { fontSize: '0.75rem', fontWeight: 600 };

const StatCard = ({ label, value, icon, color, hint }) => (
  <Card variant="outlined" sx={{ height: '100%' }}>
    <CardContent sx={{ py: 2, textAlign: 'center', '&:last-child': { pb: 2 } }}>
      <Box sx={{ color, mb: 0.5 }}>{icon}</Box>
      <Typography sx={{ fontSize: '1.3rem', fontWeight: 700 }}>{value}</Typography>
      <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{label}</Typography>
      {hint && <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary', mt: 0.25 }}>{hint}</Typography>}
    </CardContent>
  </Card>
);

const AiPlatformUsage = () => {
  const brandColor = getBrandColor();
  const headSx = getBrandedTableHeadSx();

  const [period, setPeriod] = useState(30);
  const [tab, setTab] = useState('usage');
  const [usagePage, setUsagePage] = useState(0);
  const [healthPage, setHealthPage] = useState(0);
  const [usage, setUsage] = useState(null);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    aiAssistantService.getPlatformUsage({ days: period, page: usagePage, size: PAGE_SIZE })
      .then(data => { if (!cancelled) setUsage(data); })
      .catch(err => { if (!cancelled) setError(errorText(err)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [period, usagePage, reload]);

  useEffect(() => {
    let cancelled = false;
    aiAssistantService.getPlatformHealth({ days: period, page: healthPage, size: PAGE_SIZE })
      .then(data => { if (!cancelled) setHealth(data); })
      .catch(err => { if (!cancelled) setError(errorText(err)); });
    return () => { cancelled = true; };
  }, [period, healthPage, reload]);

  const changePeriod = (e, v) => {
    if (!v) return;
    setPeriod(v);
    setUsagePage(0);
    setHealthPage(0);
  };

  const overall = health?.overall;
  const topTools = overall?.top_tools || [];
  const usageRows = usage?.typing_centers?.content || [];
  const healthRows = health?.typing_centers?.content || [];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AiIcon sx={{ color: brandColor }} />
          <Typography sx={{ fontSize: '1.1rem', fontWeight: 600 }}>AI Assistant across typing centers</Typography>
          {usage && (
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', ml: 1 }}>{usage.from} – {usage.to}</Typography>
          )}
          {loading && <CircularProgress size={16} sx={{ ml: 1 }} />}
        </Box>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={period}
          onChange={changePeriod}
          sx={{
            '& .MuiToggleButton-root': { textTransform: 'none', fontSize: '0.75rem', px: 1.5, py: 0.25 },
            '& .Mui-selected': { color: `${brandColor} !important`, bgcolor: `${brandColor}14 !important`, borderColor: `${brandColor} !important` },
          }}
        >
          {PERIODS.map(d => <ToggleButton key={d} value={d}>{d} days</ToggleButton>)}
        </ToggleButtonGroup>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} action={<Button size="small" onClick={() => setReload(r => r + 1)}>Retry</Button>}>
          {error}
        </Alert>
      )}

      {usage && (
        <>
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: 'text.secondary', mb: 1 }}>USAGE</Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={4} md={2.4}><StatCard label="Active typing centers" value={usage.active_typing_centers} icon={<TcIcon />} color={brandColor} /></Grid>
            <Grid item xs={6} sm={4} md={2.4}><StatCard label="Sessions" value={usage.sessions} icon={<SessionsIcon />} color={brandColor} /></Grid>
            <Grid item xs={6} sm={4} md={2.4}><StatCard label="Messages" value={usage.exchanges} icon={<ChatIcon />} color={brandColor} /></Grid>
            <Grid item xs={6} sm={6} md={2.4}><StatCard label="Tokens" value={formatTokens(usage.tokens)} icon={<TokenIcon />} color={brandColor} /></Grid>
            <Grid item xs={12} sm={6} md={2.4}><StatCard label="Est. cost (USD)" value={formatCost(usage.estimated_cost_usd)} icon={<CostIcon />} color={brandColor} hint="Billed to each TC's own key" /></Grid>
          </Grid>
        </>
      )}

      {overall && (
        <>
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: 'text.secondary', mb: 1 }}>HEALTH</Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={7}>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={4}>
                  <StatCard label="Error rate" value={formatRate(overall.error_rate)} icon={<ErrorIcon />}
                    color={overall.error_rate > 0.05 ? 'error.main' : brandColor} hint={`${overall.error_count} of ${overall.exchanges} messages`} />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <StatCard label="Avg response time" value={formatMs(overall.avg_response_ms)} icon={<SpeedIcon />} color={brandColor} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <StatCard label="Failed tool calls" value={overall.failed_tool_calls} icon={<ToolIcon />}
                    color={overall.failed_tool_calls > 0 ? 'warning.main' : brandColor} hint={`of ${overall.tool_calls} tool calls`} />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} md={5}>
              <Paper variant="outlined" sx={{ height: '100%', overflow: 'hidden' }}>
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, px: 2, pt: 1.5, pb: 1 }}>Top tools</Typography>
                <Table size="small">
                  <TableBody>
                    {topTools.map(t => (
                      <TableRow key={t.tool}>
                        <TableCell sx={{ ...cellSx, py: 0.5 }}>{t.tool.replace(/_/g, ' ')}</TableCell>
                        <TableCell align="right" sx={{ ...cellSx, py: 0.5 }}>{t.calls}</TableCell>
                        <TableCell align="right" sx={{ ...cellSx, py: 0.5, width: 90 }}>
                          {t.failures > 0
                            ? <Chip label={`${t.failures} failed`} size="small" color="warning" sx={{ fontSize: '0.6rem', height: 18 }} />
                            : <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>—</Typography>}
                        </TableCell>
                      </TableRow>
                    ))}
                    {topTools.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={3} sx={{ ...cellSx, color: 'text.secondary', textAlign: 'center', py: 2 }}>No tool calls in this period</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Paper>
            </Grid>
          </Grid>
        </>
      )}

      {(usage || health) && (
        <Paper sx={{ overflow: 'hidden' }}>
          <Tabs value={tab} onChange={(e, v) => setTab(v)}
            sx={{ px: 1, borderBottom: 1, borderColor: 'divider', '& .MuiTab-root': { textTransform: 'none', fontSize: '0.8rem', minHeight: 44 },
              '& .Mui-selected': { color: `${brandColor} !important` }, '& .MuiTabs-indicator': { bgcolor: brandColor } }}>
            <Tab value="usage" label="Usage by typing center" />
            <Tab value="health" label="Health by typing center" />
          </Tabs>

          {tab === 'usage' && (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={headSx}>
                      <TableCell sx={headCellSx}>Typing center</TableCell>
                      <TableCell align="center" sx={headCellSx}>Sessions</TableCell>
                      <TableCell align="center" sx={headCellSx}>Messages</TableCell>
                      <TableCell align="center" sx={headCellSx}>Tokens</TableCell>
                      <TableCell align="right" sx={headCellSx}>Est. cost</TableCell>
                      <TableCell align="right" sx={headCellSx}>Last used</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {usageRows.map(r => (
                      <TableRow key={r.typing_center_id} hover>
                        <TableCell sx={cellSx}>{r.typing_center_name || 'Unknown typing center'}</TableCell>
                        <TableCell align="center" sx={cellSx}>{r.sessions}</TableCell>
                        <TableCell align="center" sx={cellSx}>{r.exchanges}</TableCell>
                        <TableCell align="center" sx={cellSx}>{formatTokens(r.tokens)}</TableCell>
                        <TableCell align="right" sx={cellSx}>{formatCost(r.estimated_cost_usd)}</TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                          {r.last_used ? new Date(r.last_used).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                    {usageRows.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} sx={{ ...cellSx, textAlign: 'center', py: 4, color: 'text.secondary' }}>
                          No typing center used the AI Assistant in this period
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination component="div" count={usage?.typing_centers?.total_elements || 0} page={usagePage}
                onPageChange={(e, p) => setUsagePage(p)} rowsPerPage={PAGE_SIZE} rowsPerPageOptions={[PAGE_SIZE]} />
            </>
          )}

          {tab === 'health' && (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={headSx}>
                      <TableCell sx={headCellSx}>Typing center</TableCell>
                      <TableCell align="center" sx={headCellSx}>Messages</TableCell>
                      <TableCell align="center" sx={headCellSx}>Error rate</TableCell>
                      <TableCell align="center" sx={headCellSx}>Avg response</TableCell>
                      <TableCell align="center" sx={headCellSx}>Failed tools</TableCell>
                      <TableCell sx={headCellSx}>Top tools</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {healthRows.map(r => (
                      <TableRow key={r.typing_center_id} hover>
                        <TableCell sx={cellSx}>{r.typing_center_name || 'Unknown typing center'}</TableCell>
                        <TableCell align="center" sx={cellSx}>{r.exchanges}</TableCell>
                        <TableCell align="center" sx={{ ...cellSx, color: r.error_rate > 0.05 ? 'error.main' : 'inherit' }}>
                          {formatRate(r.error_rate)}
                        </TableCell>
                        <TableCell align="center" sx={cellSx}>{formatMs(r.avg_response_ms)}</TableCell>
                        <TableCell align="center" sx={{ ...cellSx, color: r.failed_tool_calls > 0 ? 'warning.dark' : 'inherit' }}>
                          {r.failed_tool_calls} / {r.tool_calls}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {(r.top_tools || []).slice(0, 3).map(t => (
                              <Chip key={t.tool} label={`${t.tool.replace(/_/g, ' ')} · ${t.calls}`} size="small"
                                sx={{ fontSize: '0.6rem', height: 18, bgcolor: 'action.selected' }} />
                            ))}
                            {(r.top_tools || []).length === 0 && <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>—</Typography>}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                    {healthRows.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} sx={{ ...cellSx, textAlign: 'center', py: 4, color: 'text.secondary' }}>
                          No AI Assistant activity in this period
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination component="div" count={health?.typing_centers?.total_elements || 0} page={healthPage}
                onPageChange={(e, p) => setHealthPage(p)} rowsPerPage={PAGE_SIZE} rowsPerPageOptions={[PAGE_SIZE]} />
            </>
          )}
        </Paper>
      )}

      <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mt: 1 }}>
        Figures only; session and correction content stays with each typing center. Costs are estimates from list prices (USD).
      </Typography>
    </Box>
  );
};

export default AiPlatformUsage;
